import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useCurrentMonthUsage } from '@/hooks/useBilling';
import { formatAmount } from '@/utils/billing.utils';
import { useTranslation } from 'react-i18next';
import { Search, Loader2, Shield, FileUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { UsageType } from '@/types/billing.types';

interface ActiveFoldersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ActiveFoldersModal({
  isOpen,
  onClose,
}: ActiveFoldersModalProps) {
  const { t } = useTranslation();
  const { data, isLoading, isFetching, error, refetch } =
    useCurrentMonthUsage();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | UsageType>('all');

  const folders = data?.folders || [];
  const vaultUsage = data?.vaultUsage;
  const activeVaults = vaultUsage?.activeVaults || [];
  const uploadedDocuments = vaultUsage?.uploadedDocuments || [];
  const foldersAmountHT = data?.foldersAmountHT ?? data?.estimatedAmountHT ?? 0;
  const estimatedAmountHT = data?.estimatedAmountHT ?? 0;
  const estimatedAmountTTC = data?.estimatedAmountTTC ?? 0;
  const vatRate = data?.vatRate ?? 20;
  const vatAmount = estimatedAmountTTC - estimatedAmountHT;
  const [activeTab, setActiveTab] = useState<
    'folders' | 'vaults' | 'documents'
  >('folders');

  // Filter folders
  const filteredFolders = folders.filter(folder => {
    const matchesSearch = folder.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesFilter =
      filterType === 'all' || folder.usageTypes.includes(filterType);
    return matchesSearch && matchesFilter;
  });

  useEffect(() => {
    if (isOpen) {
      void refetch();
    }
  }, [isOpen, refetch]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('billing.folders.title', 'Dossiers actifs')}
    >
      <div className="space-y-4">
        {/* Search and Filter */}
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              placeholder={t(
                'billing.folders.search',
                'Rechercher un dossier...'
              )}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-xl border border-neutral-300 bg-white px-10 py-2.5 text-sm focus:ring-2 focus:outline-none"
            />
          </div>
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value as 'all' | UsageType)}
            className="focus:border-primary-500 focus:ring-primary-100 rounded-xl border border-neutral-300 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:outline-none"
          >
            <option value="all">
              {t('billing.folders.allTypes', 'Tous les types')}
            </option>
            <option value="automation_executed">
              {t('billing.folders.automation', 'Automatisation')}
            </option>
            <option value="bulletin_sent">
              {t('billing.folders.bulletin', 'Bulletin')}
            </option>
            <option value="dsn_sent">{t('billing.folders.dsn', 'DSN')}</option>
            <option value="edition_generated">
              {t('billing.folders.edition', 'Édition')}
            </option>
            <option value="edition_viewed">
              {t('billing.folders.editionViewed', 'Édition consultée')}
            </option>
          </select>
        </div>

        {/* Loading State */}
        {(isLoading || isFetching) && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="text-primary-600 h-8 w-8 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-error-50 rounded-xl border border-red-200 p-6 text-center">
            <p className="text-error-700">
              {t('billing.folders.error', 'Erreur lors du chargement')}
            </p>
          </div>
        )}

        {/* Folders List */}
        {!isLoading && !error && (
          <>
            {data && (
              <div className="space-y-2 rounded-xl border border-neutral-200 bg-neutral-50 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-600">
                    {t(
                      'billing.folders.foldersLine',
                      'Dossiers actifs ({{count}} × 1€)',
                      { count: folders.length }
                    )}
                  </span>
                  <span className="font-semibold text-neutral-900">
                    {formatAmount(foldersAmountHT)}
                  </span>
                </div>
                {vaultUsage &&
                  (vaultUsage.activeVaultsCount > 0 ||
                    vaultUsage.documentsUploadedCount > 0) && (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-600">
                          {t(
                            'billing.folders.vaultsLine',
                            'Dossiers avec coffre-fort ({{count}} × 1€)',
                            { count: vaultUsage.activeVaultsCount }
                          )}
                        </span>
                        <span className="font-semibold text-neutral-900">
                          {formatAmount(vaultUsage.vaultActivationAmountHT)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-600">
                          {t(
                            'billing.folders.documentsLine',
                            'Documents déposés ({{count}} × 0,25€)',
                            { count: vaultUsage.documentsUploadedCount }
                          )}
                        </span>
                        <span className="font-semibold text-neutral-900">
                          {formatAmount(vaultUsage.documentUploadAmountHT)}
                        </span>
                      </div>
                    </>
                  )}
                <div className="flex items-center justify-between border-t border-neutral-300 pt-2 text-sm">
                  <span className="text-neutral-600">
                    {t('billing.folders.amountHT', 'Montant HT')}:
                  </span>
                  <span className="font-semibold text-neutral-900">
                    {formatAmount(estimatedAmountHT)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-600">
                    {t('billing.folders.vat', 'TVA {{rate}}%', {
                      rate: vatRate,
                    })}
                    :
                  </span>
                  <span className="font-semibold text-neutral-900">
                    {formatAmount(vatAmount)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t border-neutral-300 pt-2 text-sm">
                  <span className="font-medium text-neutral-700">
                    {t('billing.folders.total', 'Total TTC')}:
                  </span>
                  <span className="text-lg font-bold text-neutral-900">
                    {formatAmount(estimatedAmountTTC)}
                  </span>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 rounded-lg bg-neutral-100 p-1">
              <button
                onClick={() => setActiveTab('folders')}
                className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'folders'
                    ? 'bg-white text-neutral-900 shadow-sm'
                    : 'text-neutral-600 hover:text-neutral-900'
                }`}
              >
                {t('billing.tabs.folders', 'Dossiers')} ({folders.length})
              </button>
              {activeVaults.length > 0 && (
                <button
                  onClick={() => setActiveTab('vaults')}
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    activeTab === 'vaults'
                      ? 'bg-white text-neutral-900 shadow-sm'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  {t('billing.tabs.vaults', 'Coffres-forts')} (
                  {activeVaults.length})
                </button>
              )}
              {uploadedDocuments.length > 0 && (
                <button
                  onClick={() => setActiveTab('documents')}
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    activeTab === 'documents'
                      ? 'bg-white text-neutral-900 shadow-sm'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  {t('billing.tabs.documents', 'Documents')} (
                  {uploadedDocuments.length})
                </button>
              )}
            </div>

            <div className="max-h-96 space-y-2 overflow-y-auto">
              {/* Folders Tab */}
              {activeTab === 'folders' && (
                <>
                  {filteredFolders.length === 0 ? (
                    <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-8 text-center">
                      <p className="text-neutral-600">
                        {t('billing.folders.noResults', 'Aucun dossier trouvé')}
                      </p>
                    </div>
                  ) : (
                    filteredFolders.map(folder => (
                      <div
                        key={folder.uuid}
                        className="rounded-xl border border-neutral-200 bg-white p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-neutral-900">
                              {folder.name}
                            </h3>
                            <p className="mt-1 text-sm text-neutral-600">
                              {t(
                                'billing.folders.usageCount',
                                '{{count}} utilisation(s)',
                                { count: folder.usageCount }
                              )}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {folder.usageTypes.map(type => (
                                <span
                                  key={type}
                                  className="bg-primary-50 text-primary-700 rounded-full px-2 py-1 text-xs font-medium"
                                >
                                  {t(`billing.folders.type.${type}`, type)}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="font-bold text-neutral-900">
                              {formatAmount(100)} {/* 1€ HT = 100 centimes */}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}

              {/* Vaults Tab */}
              {activeTab === 'vaults' && (
                <>
                  {activeVaults.length === 0 ? (
                    <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-8 text-center">
                      <p className="text-neutral-600">
                        {t(
                          'billing.vaults.noResults',
                          'Aucun coffre-fort actif'
                        )}
                      </p>
                    </div>
                  ) : (
                    activeVaults.map(vault => (
                      <div
                        key={vault.uuid}
                        className="rounded-xl border border-neutral-200 bg-white p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <Shield className="h-4 w-4 text-violet-500" />
                              <h3 className="font-semibold text-neutral-900">
                                {vault.name}
                              </h3>
                            </div>
                            {vault.folderName && (
                              <p className="mt-1 text-sm text-neutral-600">
                                {t(
                                  'billing.vaults.linkedFolder',
                                  'Dossier : {{name}}',
                                  { name: vault.folderName }
                                )}
                              </p>
                            )}
                            <p className="mt-1 text-xs text-neutral-500">
                              {t(
                                'billing.vaults.activatedAt',
                                'Activé le {{date}}',
                                {
                                  date: new Date(
                                    vault.activatedAt
                                  ).toLocaleDateString('fr-FR'),
                                }
                              )}
                            </p>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="font-bold text-neutral-900">
                              {formatAmount(vault.unitPriceHT)}
                            </p>
                            <p className="text-xs text-neutral-500">HT</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && (
                <>
                  {uploadedDocuments.length === 0 ? (
                    <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-8 text-center">
                      <p className="text-neutral-600">
                        {t(
                          'billing.documents.noResults',
                          'Aucun document déposé'
                        )}
                      </p>
                    </div>
                  ) : (
                    uploadedDocuments.map(doc => (
                      <div
                        key={doc.uuid}
                        className="rounded-xl border border-neutral-200 bg-white p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <FileUp className="h-4 w-4 text-indigo-500" />
                              <h3 className="font-semibold text-neutral-900">
                                {doc.documentName}
                              </h3>
                            </div>
                            <p className="mt-1 text-sm text-neutral-600">
                              {doc.documentType}
                            </p>
                            <p className="mt-1 text-xs text-neutral-500">
                              {t(
                                'billing.documents.uploadedAt',
                                'Déposé le {{date}}',
                                {
                                  date: new Date(
                                    doc.uploadedAt
                                  ).toLocaleDateString('fr-FR'),
                                }
                              )}
                            </p>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="font-bold text-neutral-900">
                              {formatAmount(doc.unitPriceHT)}
                            </p>
                            <p className="text-xs text-neutral-500">HT</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4">
          <Button variant="outline" onClick={onClose}>
            {t('common.close', 'Fermer')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
